import api from '../utils/api';

/**
 * Get all unique tags from the user's contacts
 * @returns {Promise<Array>} - Array of unique tag objects
 */
export const getContactTags = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    const response = await api.get(`/contacts/tags?${params.toString()}`);
    
    // Унифицируем результат: всегда возвращаем массив строк
    if (response.data && response.data.data && Array.isArray(response.data.data)) {
      // Если { data: ['tag1', 'tag2', ...] }
      return response.data.data.filter(Boolean);
    } else if (Array.isArray(response.data)) {
      if (response.data.length && typeof response.data[0] === 'object') {
        // Если [{name: 'client'}, ...]
        return response.data.map(t => t.name).filter(Boolean);
      }
      // Если ['client', ...]
      return response.data.filter(Boolean);
    }
    
    console.log('Unexpected tags response format:', response.data);
    return [];
  } catch (error) {
    console.error('Error fetching tags:', error);
    throw error;
  }
};

/**
 * Get all contacts with optional filters
 * @param {Object} filters - Optional filters (search, tags, page, limit)
 * @returns {Promise<Object>} - Contacts data with pagination
 */
export const getContacts = async (filters = {}) => {
  try {
    const params = new URLSearchParams();

    if (filters.search) params.append('search', filters.search);
    if (filters.tags) params.append('tags', filters.tags);
    if (filters.instance_id) params.append('instance_id', filters.instance_id);
    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);

    const response = await api.get(`/contacts?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching contacts:', error);
    throw error;
  }
};

/**
 * Get a single contact by ID
 * @param {number} id - Contact ID
 * @returns {Promise<Object>} - Contact data
 */
export const getContact = async (id) => {
  try {
    const response = await api.get(`/contacts/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching contact ${id}:`, error);
    throw error;
  }
};

/**
 * Create a new contact
 * @param {Object} contactData - Contact data
 * @returns {Promise<Object>} - Created contact
 */
export const createContact = async (contactData) => {
  try {
    const response = await api.post('/contacts', contactData);
    return response.data;
  } catch (error) {
    console.error('Error creating contact:', error);
    throw error;
  }
};

/**
 * Update an existing contact
 * @param {number} id - Contact ID
 * @param {Object} contactData - Updated contact data
 * @returns {Promise<Object>} - Updated contact
 */
export const updateContact = async (id, contactData) => {
  try {
    const response = await api.put(`/contacts/${id}`, contactData);
    return response.data;
  } catch (error) {
    console.error(`Error updating contact ${id}:`, error);
    throw error;
  }
};

/**
 * Delete a contact
 * @param {number} id - Contact ID
 * @returns {Promise<Object>} - Response data
 */
export const deleteContact = async (id) => {
  try {
    const response = await api.delete(`/contacts/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting contact ${id}:`, error);
    throw error;
  }
};

/**
 * Import contacts from a file
 * @param {File} file - CSV or JSON file containing contacts
 * @returns {Promise<Object>} - Import results
 */
export const importContacts = async (file) => {
  try {
    // Create form data
    const formData = new FormData();
    formData.append('file', file);
    
    // Send request with form data
    const response = await api.post('/contacts/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error importing contacts:', error);
    throw error;
  }
};

/**
 * Export contacts
 * @param {string} format - Export format ('json' or 'csv')
 * @param {Object} filters - Optional filters (search, tags)
 * @returns {Promise<Blob>} - Exported data as blob
 */
export const exportContacts = async (format = 'json', filters = {}) => {
  try {
    const params = new URLSearchParams();
    params.append('format', format);

    if (filters.search) params.append('search', filters.search);
    if (filters.tags) params.append('tags', filters.tags);

    const response = await api.get(`/contacts/export?${params.toString()}`, {
      responseType: 'blob'
    });

    return response.data;
  } catch (error) {
    console.error('Error exporting contacts:', error);
    throw error;
  }
};

/**
 * Update tags for multiple contacts
 * @param {Array} contactIds - Array of contact IDs
 * @param {string} tags - Comma-separated list of tags
 * @param {string} operation - Operation to perform ('add', 'remove', 'invert')
 * @returns {Promise<Object>} - Result of the operation
 */
export const updateContactTags = async (contactIds, tags, operation = 'add') => {
  try {
    // Process each contact individually
    const results = {
      success: [],
      failed: []
    };

    // Convert tags string to array
    const tagsArray = tags.split(',').map(tag => tag.trim()).filter(Boolean);

    for (const contactId of contactIds) {
      try {
        // First get the current contact to access its tags
        const contact = await getContact(contactId);

        if (!contact) {
          results.failed.push({ id: contactId, error: 'Contact not found' });
          continue;
        }

        // Get current tags as array
        const currentTags = contact.tags ? 
          contact.tags.split(',').map(tag => tag.trim()).filter(Boolean) : 
          [];

        let newTags = [];

        // Perform the requested operation
        switch (operation) {
          case 'add':
            // Add new tags without duplicates
            newTags = [...new Set([...currentTags, ...tagsArray])];
            break;

          case 'remove':
            // Remove specified tags
            newTags = currentTags.filter(tag => !tagsArray.includes(tag));
            break;

          case 'invert':
            // Invert (toggle) specified tags
            newTags = currentTags.slice();

            for (const tag of tagsArray) {
              const index = newTags.indexOf(tag);
              if (index >= 0) {
                // Remove if present
                newTags.splice(index, 1);
              } else {
                // Add if not present
                newTags.push(tag);
              }
            }
            break;

          default:
            throw new Error(`Unknown operation: ${operation}`);
        }

        // Convert back to comma-separated string
        const newTagsString = newTags.join(', ');

        // Update the contact with new tags
        await updateContact(contactId, { tags: newTagsString });

        results.success.push({ id: contactId, tags: newTagsString });
      } catch (error) {
        results.failed.push({ id: contactId, error: error.message });
      }
    }

    return results;
  } catch (error) {
    console.error('Error updating contact tags:', error);
    throw error;
  }
};

export const deleteMultipleContacts = async (contactIds) => {
  try {
    const response = await api.delete('/contacts/bulk', {
      data: { contactIds }
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting multiple contacts:', error);
    throw error;
  }
};

export const deleteContactsByFilter = async (filters = {}) => {
  try {
    const payload = {};
    if (filters.search) payload.search = filters.search;
    if (filters.tags) payload.tags = Array.isArray(filters.tags) ? filters.tags.join(', ') : filters.tags;
    const response = await api.delete('/contacts/bulk-by-filter', { data: payload });
    return response.data;
  } catch (error) {
    console.error('Error deleting contacts by filter:', error);
    throw error;
  }
};
